import React from 'react';
import { Link } from 'react-router-dom';
import {
  IColumn,
  ColumnActionsMode,
  TooltipHost,
  TooltipDelay,
  ITooltipHostStyles,
} from '@fluentui/react';
import { EntityTerminationStage } from './types';
import momentUtc from '../../utils/DateFormatter';
import StatDateFormats from '../../utils/types/DateFormats';

const tooltipHostStyles: Partial<ITooltipHostStyles> = {
  root: {
    display: 'inline-block',
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};

const onRenderField = (value: string): JSX.Element => {
  return (
    <TooltipHost
      delay={TooltipDelay.medium}
      key={value}
      styles={tooltipHostStyles}
      tooltipProps={{
        onRenderContent: () => {
          return <span>{value}</span>;
        },
      }}
    >
      {value}
    </TooltipHost>
  );
};

const getColumns = (
  baseRoute: string,
  terminationLifecycle: string,
  lifecycleStatusUrlSegment: string,
): IColumn[] => {
  const commonColumns: IColumn[] = [
    {
      key: 'company',
      name: 'Company',
      minWidth: 250,
      maxWidth: 250,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => {
        const value = `${item.companyCode}-${item.entityLegalName}`;
        return (
          <TooltipHost
            delay={TooltipDelay.medium}
            key={value}
            styles={tooltipHostStyles}
            tooltipProps={{
              onRenderContent: () => {
                return <span>{value}</span>;
              },
            }}
          >
            <Link
              to={{
                pathname: `/${baseRoute}/${item.companyCode}/${lifecycleStatusUrlSegment}`,
                // Carry the originating pivot stage so the detail page's back button can return
                // the user to the same tab. Needed because multiple stages (e.g. Eligible and All)
                // share the 'overview' URL segment and can't be distinguished from the path alone.
                search: `?entityTerminationStage=${encodeURIComponent(
                  terminationLifecycle ?? 'All',
                )}`,
              }}
            >
              {value}
            </Link>
          </TooltipHost>
        );
      },
    },
    {
      key: 'country',
      name: 'Country',
      fieldName: 'physicalCountry',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.physicalCountry),
    },
    {
      key: 'terminationContacts',
      name: 'Termination Contacts',
      fieldName: 'terminationContacts',
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => {
        let contacts = '';
        if (item.companyTerminationContacts && item.companyTerminationContacts.length > 0) {
          contacts = item.companyTerminationContacts
            .map((c: { displayName: string }) => c.displayName)
            .join(', ');
        }
        return onRenderField(contacts);
      },
    },
    {
      key: 'celaTermContact',
      name: 'CELA Term Contact',
      fieldName: 'celaTermContact',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.celaTermContact?.displayName ?? ''),
    },
    {
      key: 'businessStakeholders',
      name: 'Business Stakeholders',
      fieldName: 'businessStakeholders',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) =>
        onRenderField(
          item.businessStakeholders
            ?.map((contact: { displayName: string }) => contact.displayName)
            .join(', ') ?? '',
        ),
    },
    {
      key: 'statController',
      name: 'Statutory Controller',
      fieldName: 'statController',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.statController?.displayName ?? ''),
    },
    {
      key: 'anticipatedTerminationFY',
      name: 'Anticipated FY',
      fieldName: 'anticipatedTerminationFY',
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.anticipatedTerminationFY),
    },
    {
      key: 'entityStatus',
      name: 'Legal Status',
      fieldName: 'entityStatus',
      minWidth: 150,
      maxWidth: 150,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.entityStatus),
    },
    {
      key: 'lifecycleStatus',
      name: 'Lifecycle Status',
      fieldName: 'lifecycleStatus',
      minWidth: 150,
      maxWidth: 150,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.lifecycleStatus),
    },
    {
      key: 'termLifecycleStatus',
      name: 'Term Lifecycle Status',
      fieldName: 'terminationLifecycleStatus',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.terminationLifecycleStatus),
    },
  ];

  const legalInProcessColumns: IColumn[] = [
    {
      key: 'legalInProcessOpenItemsCount',
      name: 'Legal in Process Open Items Count',
      fieldName: 'legalInProcessOpenActionItemsCount',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.legalInProcessOpenActionItemsCount),
    },
    {
      key: 'legalInProcessFollowUpRequired',
      name: 'In Process Followup Required?',
      fieldName: 'legalInProcessFollowUpRequired',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.legalInProcessFollowUpRequired ? 'Yes' : 'No'),
    },
  ];

  const blockerColumns: IColumn[] = [
    {
      key: 'primaryBlockerCategory',
      name: 'Primary Blocker Category',
      fieldName: 'primaryBlockerCategory',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(item.primaryBlockerCategory ?? ''),
    },
    {
      key: 'openBlockersCount',
      name: '# of Open Blockers',
      fieldName: 'openBlockersCount',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => onRenderField(String(item.openBlockersCount ?? 0)),
    },
  ];

  const financeIncompleteColumns: IColumn[] = [
    {
      key: 'financeIncompletePercentComplete',
      name: 'Finance Incomplete % Complete',
      fieldName: 'financeIncompletePercentComplete',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) =>
        onRenderField(
          item.financeIncompletePercentComplete === null ||
            item.financeIncompletePercentComplete === undefined
            ? ''
            : `${item.financeIncompletePercentComplete}%`,
        ),
    },
    {
      key: 'sapDeactivationDate',
      name: 'SAP Deactivation Date',
      fieldName: 'sapDeactivationDate',
      minWidth: 120,
      maxWidth: 120,
      isResizable: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) =>
        onRenderField(
          item.sapDeactivationDate
            ? momentUtc(item.sapDeactivationDate, StatDateFormats.TwoDigitDate, true)
            : '',
        ),
    },
  ];

  if (terminationLifecycle === null || terminationLifecycle === undefined) {
    return [
      ...commonColumns,
      ...blockerColumns,
      ...legalInProcessColumns,
      ...financeIncompleteColumns,
    ];
  }

  const columns: IColumn[] = [...commonColumns];

  switch (terminationLifecycle) {
    case EntityTerminationStage.LEGAL_IN_PROCESS:
      columns.push(...legalInProcessColumns);
      break;
    case EntityTerminationStage.BLOCKER:
      columns.push(...blockerColumns);
      break;
    case EntityTerminationStage.FINANCE_INCOMPLETE:
      columns.push(...financeIncompleteColumns);
      break;
    default:
      break;
  }

  return columns;
};

export default getColumns;
